import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setDialog } from 'src/features/dialog/dialogSlice';
import {
  useGetTenantInformationQuery,
  useSendTenantTestMailMutation,
  useUpdateTenantMailSettingsMutation,
} from 'src/features/tenants/tenantsApiSlice';
import { selectCurrentUserData } from 'src/features/auth/authSlice';
import MailTemplatePageLayout from './MailTemplatePageLayout';

const tokens = [
  {
    value: '{{formalGreeting}}',
    label: 'Formale Anrede',
    desc: 'Automatische Anrede basierend auf dem Titel: Herr → Sehr geehrter Herr [Nachname], Frau → Sehr geehrte Frau [Nachname], kein Titel → Guten Tag [Vollständiger Name].',
  },
  {
    value: '{{memberFirstName}}',
    label: 'Vorname',
    desc: 'Vorname des Mitglieds.',
  },
  {
    value: '{{memberLastName}}',
    label: 'Nachname',
    desc: 'Nachname des Mitglieds.',
  },
  {
    value: '{{memberFullName}}',
    label: 'Vollständiger Name',
    desc: 'Vor- und Nachname des Mitglieds.',
  },
  {
    value: '{{contractOwner}}',
    label: 'Vertragsinhaber',
    desc: 'Name der Person, die den Vertrag abgeschlossen hat.',
  },
  {
    value: '{{contractEmail}}',
    label: 'E-Mail-Adresse',
    desc: 'Die im Vertrag hinterlegte E-Mail-Adresse.',
  },
  {
    value: '{{activationUrl}}',
    label: 'Aktivierungslink',
    desc: 'Link zur Kontoaktivierung für das Mitglieder-Portal – wird automatisch generiert.',
  },
  {
    value: '{{tenantName}}',
    label: 'Vereinsname',
    desc: 'Name Ihres Vereins.',
  },
  {
    value: '{{today}}',
    label: 'Heutiges Datum',
    desc: 'Das aktuelle Datum zum Zeitpunkt des E-Mail-Versands.',
  },
];

const MailRegistrationTemplateSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const currentUserData = useSelector(selectCurrentUserData);
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const [updateTenantMailSettings, { isLoading: isSaving }] =
    useUpdateTenantMailSettingsMutation();
  const [sendTenantTestMail, { isLoading: isTesting }] =
    useSendTenantTestMailMutation();

  const defaultValues = useMemo(
    () => ({
      registrationSubject:
        tenantInfo?.settings?.mail?.registrationSubject || 'Anmeldebestätigung',
      registrationTemplate:
        tenantInfo?.settings?.mail?.registrationTemplate ||
        '{{formalGreeting}},\n\nvielen Dank für Ihre Anmeldung. Wir freuen uns, {{memberFullName}} als Mitglied bei {{tenantName}} begrüßen zu dürfen.\n\nMit freundlichen Grüßen\n{{tenantName}}',
      registrationTemplateMode:
        tenantInfo?.settings?.mail?.registrationTemplateMode || 'auto',
      registrationBcc: tenantInfo?.settings?.mail?.registrationBcc || '',
      testRecipient: currentUserData?.mail || '',
    }),
    [tenantInfo, currentUserData]
  );

  const { register, handleSubmit, control, reset, setValue, getValues } =
    useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleInsertToken = (token) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, token);
      const next = editorRef.current.innerHTML || '';
      setValue('registrationTemplate', next, { shouldDirty: true });
      return;
    }
    const current = getValues('registrationTemplate') || '';
    setValue('registrationTemplate', current ? `${current} ${token}` : token, {
      shouldDirty: true,
    });
  };

  const onSubmit = async (data) => {
    const { testRecipient, ...mailSettings } = data;
    try {
      const result = await updateTenantMailSettings(mailSettings).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Einstellungen gespeichert',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message ||
              'Einstellungen konnten nicht gespeichert werden.',
          },
        })
      );
    }
  };

  const handleSendTest = async () => {
    const recipient = (getValues('testRecipient') || '').trim();
    if (!recipient) {
      dispatch(
        setDialog({
          title: 'Hinweis',
          type: 'ERROR',
          data: { errorText: 'Bitte eine Test-E-Mail-Adresse angeben.' },
        })
      );
      return;
    }
    try {
      const result = await sendTenantTestMail({ recipient }).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Test-E-Mail gesendet',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message || 'Test-E-Mail konnte nicht gesendet werden.',
          },
        })
      );
    }
  };

  if (isLoading) return null;

  return (
    <MailTemplatePageLayout
      title='Anmeldebestätigung'
      description='Individualisieren Sie den Text der Anmeldebestätigung, die nach einer manuellen Mitgliederanlage durch einen Admin versendet wird.'
      subjectFieldName='registrationSubject'
      templateFieldName='registrationTemplate'
      bccFieldName='registrationBcc'
      templateType='registration'
      modeFieldName='registrationTemplateMode'
      currentHtmlTemplate={tenantInfo?.settings?.mail?.registrationHtmlTemplate}
      tokens={tokens}
      control={control}
      register={register}
      handleInsertToken={handleInsertToken}
      editorRef={editorRef}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      handleSendTest={handleSendTest}
      isSaving={isSaving}
      isTesting={isTesting}
      tenantName={tenantInfo?.tenantName}
      navigate={navigate}
    />
  );
};

export default MailRegistrationTemplateSettings;
